import { HttpClient } from '@wix/http-client';
import EventEmitter from 'eventemitter3';
import { isEmpty } from 'ramda';
import { ContentType } from '../constants/ContentType';
import { UploadEvent } from '../constants/upload-event';
import { UploadStatus } from '../constants/upload-status';
import { toErrorObject } from './mappers';
import { encodeParams, extendDeep, get, noop, normalizeUri, removeUndefinedValues } from './utils';
const defaultOptions = {
  withCredentials: false,
  cache: true,
  headers: {
    Accept: 'application/json, text/plain, */*'
  }
};
const httpClient = new HttpClient();
export const isAborted = error => httpClient.isCancel(error);
export const createAbortHandler = source => function (message) {
  if (message === void 0) {
    message = 'aborted by user';
  }
  return source.cancel(message);
};
const parseSuccessResponse = response => {
  const {
    data,
    status,
    statusText,
    request,
    headers = {}
  } = response;
  const newResponse = {
    request,
    data: typeof data === 'string' ? {
      message: data
    } : data,
    status,
    statusText,
    // TODO: expose Content-Length header
    responseSize: headers['Content-Length'] || get(request, 'responseText.length', 1)
  };
  const uploadTimingsHeader = headers['x-upload-timings'];
  if (uploadTimingsHeader) {
    newResponse.uploadTimingsHeaders = uploadTimingsHeader;
  }
  return newResponse;
};
const parseErrorResponse = error => {
  if (isAborted(error)) {
    return Promise.reject(error);
  }
  let errorObject = {};
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    errorObject = {
      name: 'MediaManagerSDKResponseError',
      config: error.response.config,
      status: error.response.status,
      statusText: error.response.statusText,
      message: get(error.response.data, ['error_description'], error.response.data),
      code: error.response.status,
      data: error.response.data,
      request: error.request,
      errorInfo: {
        key: get(error, 'response.request.responseText', 'load_error')
      }
    };
  } else if (error.request) {
    // The request was made but no response was received
    errorObject = {
      name: 'MediaManagerSDKNoResponseError',
      config: error.config,
      status: error.request.status,
      statusText: error.request.statusText,
      message: error.message,
      stack: error.stack,
      code: error.request.status,
      request: error.request,
      errorInfo: {
        key: get(error, 'request.statusText', 'load_error')
      }
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    errorObject = {
      name: 'MediaManagerSDKSendError',
      config: error.config,
      status: 0,
      statusText: '',
      message: error.message,
      stack: error.stack,
      code: 0,
      errorInfo: {
        key: 'load_error'
      }
    };
  }
  return Promise.reject(toErrorObject(errorObject));
};
function encodeRequestData(data, contentType) {
  data = removeUndefinedValues(data);
  if (contentType.indexOf(ContentType.JSON) !== -1) {
    return JSON.stringify(data);
  }
  if (contentType.indexOf(ContentType.UrlEncoded) !== -1) {
    return encodeParams(data);
  }
  if (contentType.indexOf(ContentType.Multipart) !== -1) {
    const formData = new FormData();
    Object.keys(data).forEach(function (name) {
      const value = data[name];
      const args = name === 'file' ? [name, value, value.name] : [name, value];
      formData.append.apply(formData, args);
    });
    return formData;
  }
}
export const extractCancelAndProgressConfig = function (_temp) {
  let {
    abort,
    cancelToken,
    progressEventEmitter,
    tusOptions
  } = _temp === void 0 ? {} : _temp;
  const cancelTokenAndProgressObject = {};
  if (!cancelToken || !abort || !tusOptions) {
    const source = httpClient.CancelToken.source();
    cancelTokenAndProgressObject.abort = createAbortHandler(source);
    cancelTokenAndProgressObject.cancelToken = source.token;
    cancelTokenAndProgressObject.tusOptions = {};
  } else {
    cancelTokenAndProgressObject.abort = abort;
    cancelTokenAndProgressObject.cancelToken = cancelToken;
    cancelTokenAndProgressObject.tusOptions = tusOptions;
  }
  cancelTokenAndProgressObject.progressEventEmitter = progressEventEmitter || new EventEmitter();
  return cancelTokenAndProgressObject;
};
export function initHttp(requestOptions) {
  if (requestOptions === void 0) {
    requestOptions = {};
  }
  let overrideRequestOptions = {};
  let shouldResetRequestOptions = false;
  let setRequestOptionsOnceAgain = noop;
  function setRequestOptionsOnce(settings) {
    shouldResetRequestOptions = true;
    overrideRequestOptions = settings;
    setRequestOptionsOnceAgain = () => {
      shouldResetRequestOptions = true;
      overrideRequestOptions = settings;
    };
  }
  function makeRequest(method, url, options, cancelAndProgressConfig) {
    if (cancelAndProgressConfig === void 0) {
      cancelAndProgressConfig = {};
    }
    if (options.cache === false) {
      url += `${url.indexOf('?') === -1 ? '?' : '&'}_=${Date.now()}`;
    }
    if (shouldResetRequestOptions) {
      overrideRequestOptions = {};
      shouldResetRequestOptions = false;
    }
    cancelAndProgressConfig = extractCancelAndProgressConfig(cancelAndProgressConfig);
    if (!options.withProgress) {
      cancelAndProgressConfig.progressEventEmitter.emit(UploadEvent.Upload, {
        status: UploadStatus.Uploading,
        total: 100,
        loaded: 0
      });
    }
    const disableXsrf = Boolean(requestOptions.disableXsrf);
    const httpSettings = {
      url: normalizeUri(url),
      method,
      headers: options.headers || {},
      params: !isEmpty(options.params) ? options.params : undefined,
      data: options.data,
      withCredentials: options.withCredentials,
      cancelToken: cancelAndProgressConfig.cancelToken,
      ...(disableXsrf ? {
        xsrfCookieName: ''
      } : {})
    };
    if (options.withProgress) {
      httpSettings.onUploadProgress = event => {
        const progress = Math.round(event.lengthComputable ? event.loaded * 100 / event.total : 0);
        cancelAndProgressConfig.progressEventEmitter.emit(UploadEvent.Upload, {
          status: progress <= 99 ? UploadStatus.Uploading : UploadStatus.Processing,
          total: event.total,
          loaded: event.loaded
        });
      };
    }
    const httpPromise = httpClient.request(httpSettings).then(response => {
      if (!options.withProgress) {
        cancelAndProgressConfig.progressEventEmitter.emit(UploadEvent.Upload, {
          status: UploadStatus.Processing,
          total: 100,
          loaded: 100
        });
      }
      return parseSuccessResponse(response);
    }, parseErrorResponse);
    httpPromise.cancelAndProgressConfig = cancelAndProgressConfig;
    return httpPromise;
  }
  function getRequest(url, params, options, cancelAndProgressConfig) {
    params = removeUndefinedValues({
      ...params,
      ...requestOptions.params,
      ...overrideRequestOptions.params
    });
    options = extendDeep(defaultOptions, requestOptions, options, {
      params
    });
    return makeRequest('get', url, options, cancelAndProgressConfig);
  }
  function createRequestMethod(method) {
    if (method.toLowerCase() === 'get') {
      throw new Error('please use "getRequest" function instead. This function is for other methods');
    }
    return function (url, data, params, options, cancelAndProgressConfig) {
      data = data || {};
      params = params || {};
      options = options || {};
      const userOptions = extendDeep(defaultOptions, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      }, requestOptions, options);

      // we don't need requestOptions.params in "params"
      delete userOptions.params;
      data = {
        ...data,
        ...requestOptions.params,
        ...overrideRequestOptions.params
      };
      data = encodeRequestData(data, userOptions.headers['Content-Type']);
      options = {
        params,
        ...userOptions,
        data
      };
      return makeRequest(method, url, options, cancelAndProgressConfig);
    };
  }

  /**
   * POST request
   * @param url {String}
   * @param data {Object} Post data
   * @param params {Object} Url get params
   * @param options {Object} Options
   * @returns {*}
   */
  const postRequest = createRequestMethod('post');
  const putRequest = createRequestMethod('put');
  const delRequest = createRequestMethod('delete');
  const headRequest = createRequestMethod('head');
  function upload(url, multipart, options, cancelAndProgressConfig) {
    const userOptions = extendDeep(defaultOptions, options);
    const multipartData = encodeRequestData({
      ...multipart,
      ...requestOptions.params
    }, ContentType.Multipart);
    return makeRequest('post', url, {
      ...userOptions,
      data: multipartData,
      withProgress: true
    }, cancelAndProgressConfig);
  }
  function catchError(method, onError) {
    return function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      const self = this;
      const firstPromise = callOriginalMethod();
      function callOriginalMethod() {
        return method.apply(self, args);
      }
      const promise = new Promise(function (resolve, reject) {
        firstPromise.then(resolve, function (reason) {
          if (onError !== undefined) {
            return onError.call(this, reason, callOriginalMethod);
          }
          return Promise.reject(reason);
        }).then(resolve, reject);
      });
      promise.cancelAndProgressConfig = firstPromise.cancelAndProgressConfig;
      return promise;
    };
  }
  function makeRetriable(method) {
    return catchError(method, (reason, callOriginalMethod) => {
      if (isAborted(reason) || reason.code === 406 || reason.code === 403) {
        return Promise.reject(reason);
      }
      setRequestOptionsOnceAgain();
      return callOriginalMethod();
    });
  }
  return {
    get: makeRetriable(getRequest),
    post: makeRetriable(postRequest),
    put: makeRetriable(putRequest),
    del: makeRetriable(delRequest),
    head: headRequest,
    upload: makeRetriable(upload),
    catchError,
    setRequestOptionsOnce
  };
}